/**
 * Platform foundations setup — team composition + taxonomy/location/tags gates.
 * No Team entity: real AuthAccounts scored against slot mins.
 * Roles = capability-pack bundles (see identity/capability-packs).
 */

import { SEEDED_ROLE_NAMES } from '../identity/capability-packs.js';

/** Seeded platform role names (SSOT: ROLE_PACKS / SEEDED_ROLE_NAMES). */
export const SETUP_SEEDED_ROLE_NAMES: readonly string[] = SEEDED_ROLE_NAMES;

/**
 * Platform always ensures these role rows (seed + ensure-system).
 * STOREFRONT required for boutique inscription; SHOWROOM_STAFF = caissier.
 * Other ROLE_PACKS names stay suggestions for custom create.
 */
export const SETUP_ENSURED_ROLE_NAMES: readonly string[] = Object.freeze([
  'SUPER_ADMIN',
  'SHOWROOM_STAFF',
  'STOREFRONT',
]);

/**
 * Roles scored for setup Équipe staffing (admin-assignable).
 * Exclude SUPER_ADMIN (bootstrap), STOREFRONT + WEB (not staffing targets).
 */
export const SETUP_STAFF_ROLE_NAMES: readonly string[] = Object.freeze(
  SETUP_SEEDED_ROLE_NAMES.filter(
    (n) => n !== 'SUPER_ADMIN' && n !== 'STOREFRONT' && n !== 'WEB',
  ),
);

/**
 * Optional ROLE_PACKS templates shown under « Modèles suggérés ».
 * Install via POST /admin/roles/ensure-suggested — not auto-created by ensure-system.
 */
export const SETUP_ROLE_SUGGESTION_NAMES: readonly string[] = Object.freeze(
  [...SETUP_STAFF_ROLE_NAMES, 'WEB'].filter(
    (n) => !(SETUP_ENSURED_ROLE_NAMES as readonly string[]).includes(n),
  ),
);
export type TeamSlotStatus = {
  /** Role name when scoring defined roles. */
  id: string;
  labelFr: string;
  min: number;
  count: number;
  met: boolean;
};

export type TeamCompositionStatus = {
  slots: readonly TeamSlotStatus[];
  /** All slot mins met. */
  slotsMet: boolean;
  /** At least two active staff (équipe ≠ bootstrap SA alone). */
  staffFloorMet: boolean;
  /** slotsMet ∧ staffFloorMet */
  met: boolean;
  activeStaffCount: number;
};

/** Min active staff for “équipe” (beyond seed SA). */
export const SETUP_TEAM_MIN_STAFF = 2;

function finalizeTeamStatus(
  slots: TeamSlotStatus[],
  activeStaffCount: number,
): TeamCompositionStatus {
  const slotsMet = slots.length === 0 ? false : slots.every((s) => s.met);
  const staffFloorMet = activeStaffCount >= SETUP_TEAM_MIN_STAFF;
  return {
    slots,
    slotsMet,
    staffFloorMet,
    met: slotsMet && staffFloorMet,
    activeStaffCount,
  };
}

/**
 * Setup Équipe: one staffing row per defined role (templates and/or custom).
 * Min 1 account holding that role name. Empty role list → incomplete.
 */
export function scoreTeamByDefinedRoles(
  definedRoleNames: readonly string[],
  accountsRoleNames: readonly (readonly string[])[],
): TeamCompositionStatus {
  const activeStaffCount = accountsRoleNames.length;
  const slots: TeamSlotStatus[] = definedRoleNames.map((name) => {
    let count = 0;
    for (const roles of accountsRoleNames) {
      if (roles.includes(name)) count += 1;
    }
    return {
      id: name,
      labelFr: name,
      min: 1,
      count,
      met: count >= 1,
    };
  });
  return finalizeTeamStatus(slots, activeStaffCount);
}

export type FoundationsSignals = {
  seededRoleCount: number;
  seededRoleExpected: number;
  /** Non-seeded roles (personalized create / bulk import). */
  customRoleCount: number;
  markeCount: number;
  productTypeCount: number;
  categoryCount: number;
  locationCount: number;
  /** Active SHOWROOM_OUTLET rows — required before caissier can open POS. */
  showroomOutletCount: number;
  /** Families with ≥1 non-deleted tag (Configuration initiale Tags). */
  tagFamilyWithValuesCount: number;
  activeStaffCount: number;
  team: TeamCompositionStatus;
};

export type FoundationsStepId =
  | 'roles'
  | 'locations'
  | 'team'
  | 'markes'
  | 'structure'
  | 'tags'
  | 'pref_low_stock'
  | 'pref_near_expiry';

/** Custom role ≥1, or full ROLE_PACKS install (ensure-system + ensure-suggested). */
export function isRolesFoundationComplete(s: FoundationsSignals): boolean {
  if (s.customRoleCount >= 1) return true;
  const expected = s.seededRoleExpected > 0 ? s.seededRoleExpected : SETUP_SEEDED_ROLE_NAMES.length;
  return s.seededRoleCount >= expected;
}

/**
 * Équipe staffing targets: installed staff templates + customs only.
 * Missing templates stay suggestions on « Installer les modèles » — not Créer slots.
 */
export function setupTeamRoleNames(
  presentSeededRoleNames: readonly string[] = [],
  customRoleNames: readonly string[] = [],
): string[] {
  const present = new Set(
    presentSeededRoleNames.map((n) => String(n ?? '').trim()).filter(Boolean),
  );
  const out = SETUP_STAFF_ROLE_NAMES.filter((n) => present.has(n));
  const seeded = new Set(SETUP_SEEDED_ROLE_NAMES);
  for (const name of customRoleNames) {
    const n = String(name ?? '').trim();
    if (!n || seeded.has(n) || out.includes(n)) continue;
    out.push(n);
  }
  return out;
}

export function isTeamFoundationComplete(s: FoundationsSignals): boolean {
  return s.team.met;
}

export function isMarkesFoundationComplete(s: FoundationsSignals): boolean {
  return s.markeCount >= 1;
}

export function isProductTypesFoundationComplete(s: FoundationsSignals): boolean {
  return s.productTypeCount >= 1;
}

export function isCategoriesFoundationComplete(s: FoundationsSignals): boolean {
  return s.categoryCount >= 1;
}

/** Product types + categories together (setup Structure step). */
export function isStructureFoundationComplete(s: FoundationsSignals): boolean {
  return isProductTypesFoundationComplete(s) && isCategoriesFoundationComplete(s);
}

/** All taxonomy facets (marque + type + category). */
export function isTaxonomyFoundationComplete(s: FoundationsSignals): boolean {
  return isMarkesFoundationComplete(s) && isStructureFoundationComplete(s);
}

/** Need at least one SHOWROOM_OUTLET so caissier POS can open a till. */
export function isLocationsFoundationComplete(s: FoundationsSignals): boolean {
  return s.showroomOutletCount >= 1;
}

/** ≥1 tag family with ≥1 tag (ensure-seed or manual). */
export function isTagsFoundationComplete(s: FoundationsSignals): boolean {
  return s.tagFamilyWithValuesCount >= 1;
}

/** Soft preference steps — confirm/skip; never block Continuer / foundations. */
export function isSoftPreferenceStepComplete(_s: FoundationsSignals): boolean {
  return true;
}

/** @deprecated Use isSoftPreferenceStepComplete — kept for call sites. */
export function isPreferencesFoundationComplete(s: FoundationsSignals): boolean {
  return isSoftPreferenceStepComplete(s);
}

export function isFoundationStepComplete(
  step: FoundationsStepId,
  s: FoundationsSignals,
): boolean {
  switch (step) {
    case 'roles':
      return isRolesFoundationComplete(s);
    case 'locations':
      return isLocationsFoundationComplete(s);
    case 'team':
      return isTeamFoundationComplete(s);
    case 'markes':
      return isMarkesFoundationComplete(s);
    case 'structure':
      return isStructureFoundationComplete(s);
    case 'tags':
      return isTagsFoundationComplete(s);
    case 'pref_low_stock':
    case 'pref_near_expiry':
      return isSoftPreferenceStepComplete(s);
    default:
      return false;
  }
}

export function isFoundationsComplete(s: FoundationsSignals): boolean {
  return (
    isRolesFoundationComplete(s) &&
    isLocationsFoundationComplete(s) &&
    isTeamFoundationComplete(s) &&
    isTaxonomyFoundationComplete(s) &&
    isTagsFoundationComplete(s)
  );
}

/** Locations before équipe so caissier never hits empty inventaire on first POS open. Tags after Structure. */
export const FOUNDATIONS_STEP_IDS: readonly FoundationsStepId[] = [
  'roles',
  'locations',
  'team',
  'markes',
  'structure',
  'tags',
  'pref_low_stock',
  'pref_near_expiry',
] as const;

export function foundationsProgressPercent(s: FoundationsSignals): number {
  const done = FOUNDATIONS_STEP_IDS.filter((id) => isFoundationStepComplete(id, s)).length;
  return Math.round((100 * done) / FOUNDATIONS_STEP_IDS.length);
}

export function emptyFoundationsSignals(
  partial?: Partial<Omit<FoundationsSignals, 'team'>> & {
    team?: TeamCompositionStatus;
  },
): FoundationsSignals {
  const team =
    partial?.team ??
    scoreTeamByDefinedRoles([], []);
  return {
    seededRoleCount: partial?.seededRoleCount ?? 0,
    seededRoleExpected: partial?.seededRoleExpected ?? SETUP_SEEDED_ROLE_NAMES.length,
    customRoleCount: partial?.customRoleCount ?? 0,
    markeCount: partial?.markeCount ?? 0,
    productTypeCount: partial?.productTypeCount ?? 0,
    categoryCount: partial?.categoryCount ?? 0,
    locationCount: partial?.locationCount ?? 0,
    showroomOutletCount: partial?.showroomOutletCount ?? 0,
    tagFamilyWithValuesCount: partial?.tagFamilyWithValuesCount ?? 0,
    activeStaffCount: partial?.activeStaffCount ?? team.activeStaffCount,
    team,
  };
}
